<template>
  <div class="llm-chat-container">
    <!-- Chat list sidebar -->
    <div v-if="shouldShowSidebar" class="chat-sidebar" :class="{ 'collapsed': sidebarCollapsed }">
      <div class="sidebar-header">
        <div class="sidebar-title">
          <el-button 
            type="text" 
            size="small" 
            @click="toggleSidebar"
            :icon="sidebarCollapsed ? 'el-icon-arrow-right' : 'el-icon-arrow-left'"
            class="collapse-btn">
          </el-button>
          <h4 v-if="!sidebarCollapsed">Chat History</h4>
        </div>
        <el-button 
          v-if="!sidebarCollapsed"
          type="primary" 
          size="small" 
          @click="startNewChat"
          icon="el-icon-plus">
          New Chat
        </el-button>
      </div>
      <div v-if="!sidebarCollapsed" class="chat-list">
        <div v-for="chat in chatList" 
             :key="chat.id" 
             :class="['chat-item', { 'active': chat.id === chatId }]"
             @click="selectChat(chat.id)">
          <div class="chat-name">{{ chat.name || 'Untitled Chat' }}</div>
          <div class="chat-date">{{ formatDate(chat.updated_at) }}</div>
        </div>
      </div>
    </div>
    
    <!-- Main chat area -->
    <div class="chat-main">
      <!-- Chat name header (only show if we have a specific chat) -->
      <div v-if="chatId && currentChatName" class="chat-header">
        <div class="chat-header-content">
          <div class="chat-title-section">
            <h3 v-if="!editingTitle" @click="startEditingTitle" class="chat-title-editable">
              {{ currentChatName }}
            </h3>
            <el-input 
              v-if="editingTitle"
              v-model="editingTitleValue"
              ref="titleInput"
              @blur="finishEditingTitle"
              @keyup.enter.native="finishEditingTitle"
              @keyup.esc.native="cancelEditingTitle"
              size="medium"
              class="title-input">
            </el-input>
          </div>
          <el-button 
            type="danger" 
            size="small" 
            icon="el-icon-delete"
            @click="confirmDeleteChat"
            class="delete-btn">
          </el-button>
        </div>
      </div>
      
      <!-- Chat history display -->
      <div class="chat-messages" ref="chatMessages">
        <div v-if="messages.length === 0" class="empty-chat">
          <i class="el-icon-chat-line-round empty-icon"></i>
          <p>Loading chat</p>
        </div>
        
        <div v-for="(message, index) in messages" 
             :key="index" 
             :class="['message', { 'user-message': message.isUser, 'ai-message': !message.isUser, 'system-message': message.isSystemMessage, 'new-system-message': message.isNewSystemMessage, 'old-system-message': message.isOldSystemMessage }]">
          <div v-if="!message.isSystemMessage" class="message-left-column">
            <div class="message-avatar">
              <el-avatar v-if="message.isUser" src="/images/llm/david-mirrors.jpeg" :size="40" />
              <el-avatar v-else src="/images/llm/CcChatAgentViaChatGPT.png" :size="40" />
            </div>
            <!-- Feedback buttons for AI outputs - positioned under avatar -->
            <div v-if="!message.isUser && message.output" class="feedback-buttons-vertical">
              <el-button 
                type="text" 
                size="small"
                @click="submitFeedback(message, 10)"
                :loading="message.feedbackLoading"
                class="feedback-btn thumbs-up">
                <img 
                  :src="message.userFeedback && message.userFeedback.rating === 10 ? '/images/llm/thumbs-up-selected.svg' : '/images/llm/thumbs-up-deselected.svg'"
                  alt="Thumbs up"
                  class="feedback-icon"
                />
              </el-button>
              <el-button 
                type="text" 
                size="small"
                @click="submitFeedback(message, 0)"
                :loading="message.feedbackLoading"
                class="feedback-btn thumbs-down">
                <img 
                  :src="message.userFeedback && message.userFeedback.rating === 0 ? '/images/llm/thumbs-down-selected.svg' : '/images/llm/thumbs-down-deselected.svg'"
                  alt="Thumbs down"
                  class="feedback-icon"
                />
              </el-button>
            </div>
          </div>
          <div class="message-content">
            <div class="message-header">
              <div v-if="!message.isSystemMessage">
                <span class="message-sender">{{ message.isUser ? 'You' : (message.isSystemMessage ? 'System' : 'Chimera Charlie') }}</span>
                <span class="message-time">{{ formatTime(message.timestamp) }}</span>
              </div>
            </div>
            <div v-if="message.text" class="message-text" v-html="message.text"></div>
            
            <!-- Input field for TYPE_REQUEST_USER system messages -->
            <div v-if="message.isSystemMessage && message.type === 'request-user' && !message.response" class="system-input-container">
              <div class="input-row">
                <input 
                  v-model="systemMessageResponses[message.systemMsgId]"
                  type="text" 
                  class="system-input"
                  placeholder="Enter your response..."
                  @keyup.enter="submitSystemResponse(message.systemMsgId)"
                >
                <button 
                  @click="submitSystemResponse(message.systemMsgId)"
                  class="submit-btn"
                  :disabled="!systemMessageResponses[message.systemMsgId] || systemMessageResponses[message.systemMsgId].trim() === ''"
                >
                  Submit
                </button>
              </div>
            </div>
            
            <!-- Show submitted response -->
            <div v-if="message.isSystemMessage && message.response" class="system-response">
              <strong>Your response:</strong> {{ message.response }}
            </div>
            <div v-else-if="message.job_chain_id">
              <llm-chat-crawl
                :job-chain-id="message.job_chain_id"
                :auto-start="true"
                @crawl-completed="markCrawlCompleted(message.job_chain_id)"
                @progress-update="onFirstProgressUpdate"
              />
            </div>
            <div v-else-if="message.output && message.output.type===2">
              <SingleFieldBarChart
                :chart_config="message.output.chart_config"
                :extent_id="extentId"
                :height="300"
                :width="800"
              />
            </div>
            <div v-else-if="message.output">
              <div class="markdown">
                <span v-html="message.output.html"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Input field for questions -->
      <div class="chat-input">
        <div class="input-row">
          <div class="mode-selector">
            <el-select 
              v-model="selectedMode" 
              placeholder="Select Mode"
              size="medium"
              class="mode-dropdown">
              <el-option
                label="Content Analysis"
                value="content-analysis">
              </el-option>
              <el-option
                label="Semantic Site Search"
                value="semantic-site-search">
              </el-option>
            </el-select>
          </div>
          <el-input
            ref="chatInput"
            v-model="question"
            :placeholder="chatInputPlaceholder"
            :disabled="isChatInputDisabled"
            @keyup.enter.native="submitQuestion"
            class="question-input">
            <el-button 
              slot="append" 
              :loading="loading" 
              :disabled="isChatInputDisabled"
              @click="submitQuestion"
              type="primary"
              icon="el-icon-s-promotion">
              Send
            </el-button>
          </el-input>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'LlmChat',
  components: {
    LlmChatCrawl: () => import('./LlmChatCrawl.vue')
  },
  props: {
    extentId: {
      type: Number,
      required: true
    },
    initialChatId: {
      type: Number,
      default: null
    },
    startingUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      messages: [],
      question: '',
      loading: false,
      chatId: this.initialChatId,
      historyLoaded: false,
      chatList: [],
      sidebarCollapsed: false,
      currentChatName: null,
      editingTitle: false,
      editingTitleValue: '',
      originalTitleValue: '',
      systemPromptInterval: null,
      hasReceivedFirstProgressUpdate: false,
      systemMessageResponses: {}, // Track responses for system messages that need user input
      selectedMode: 'content-analysis' // Default mode
    };
  },
  computed: {
    /**
     * Determines if the sidebar should be shown
     * Hide if there's only one chat and it's the current one
     */
    shouldShowSidebar() {
      if (this.chatList.length === 0) return false;
      if (this.chatList.length === 1 && this.chatList[0].id === this.chatId) return false;
      return true;
    },
    
    /**
     * Check if input should be disabled due to running crawls
     */
    isChatInputDisabled() {
      return this.loading || this.hasRunningCrawls();
    },
    
    /**
     * Get placeholder text for the input field
     */
    chatInputPlaceholder() {
      if (this.hasRunningCrawls()) {
        return "You will be able to ask questions about the website here once the crawl is complete";
      }
      return "Type your question here...";
    }
  },
  methods: {
    /**
     * Loads the list of chats for this extent
     */
    async loadChatList() {
      try {
        const response = await axios.get(`/llm/chat/${this.extentId}/list`);
        this.chatList = response.data.chats || [];
      } catch (error) {
        console.error('Error loading chat list:', error);
      }
    },
    
    /**
     * Selects a specific chat and navigates to it
     */
    selectChat(chatId) {
      if (chatId === this.chatId) return; // Already selected
      
      // Navigate to the selected chat
      window.location.href = `/llm/chat/${this.extentId}/${chatId}`;
    },
    
    /**
     * Starts a new chat
     */
    startNewChat() {
      window.location.href = `/llm/chat/${this.extentId}`;
    },
    
    /**
     * Toggles the sidebar collapsed state
     */
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    },
    
    /**
     * Starts editing the chat title
     */
    startEditingTitle() {
      this.editingTitle = true;
      this.editingTitleValue = this.currentChatName;
      this.originalTitleValue = this.currentChatName;
      this.$nextTick(() => {
        if (this.$refs.titleInput) {
          this.$refs.titleInput.focus();
          this.$refs.titleInput.select();
        }
      });
    },
    
    /**
     * Finishes editing the chat title and saves changes
     */
    async finishEditingTitle() {
      if (this.editingTitleValue.trim() === '' || this.editingTitleValue === this.originalTitleValue) {
        this.cancelEditingTitle();
        return;
      }
      
      try {
        await axios.patch(`/llm/chat/${this.extentId}/${this.chatId}/name`, {
          name: this.editingTitleValue.trim()
        });
        
        // Update local state
        this.currentChatName = this.editingTitleValue.trim();
        
        // Update chat list
        const chatIndex = this.chatList.findIndex(chat => chat.id === this.chatId);
        if (chatIndex !== -1) {
          this.chatList[chatIndex].name = this.currentChatName;
        }
        
        this.editingTitle = false;
      } catch (error) {
        console.error('Error updating chat name:', error);
        this.cancelEditingTitle();
      }
    },
    
    /**
     * Cancels editing the chat title
     */
    cancelEditingTitle() {
      this.editingTitle = false;
      this.editingTitleValue = '';
      this.originalTitleValue = '';
    },
    
    /**
     * Confirms and deletes the current chat
     */
    confirmDeleteChat() {
      this.$confirm(
        `Are you sure you want to delete the chat "${this.currentChatName}"? This action cannot be undone.`,
        'Delete Chat',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning',
          confirmButtonClass: 'el-button--danger'
        }
      ).then(() => {
        this.deleteChat();
      }).catch(() => {
        // User cancelled
      });
    },
    
    /**
     * Deletes the current chat
     */
    async deleteChat() {
      try {
        await axios.delete(`/llm/chat/${this.extentId}/${this.chatId}`);
        
        // Redirect to main chat page
        window.location.href = `/llm/chat/${this.extentId}`;
      } catch (error) {
        console.error('Error deleting chat:', error);
        this.$message.error('Failed to delete chat');
      }
    },
    
    /**
     * Formats a date for display in the chat list
     */
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        return 'Today';
      } else if (diffDays === 2) {
        return 'Yesterday';
      } else if (diffDays <= 7) {
        return `${diffDays - 1} days ago`;
      } else {
        return date.toLocaleDateString();
      }
    },
    
    /**
     * Loads chat history from the server when a chat ID is provided
     */
    async loadChatHistory() {
      if (!this.chatId) return;
      
      this.loading = true;
      
      try {
        const response = await axios.get(`/llm/chat/${this.extentId}/${this.chatId}/history`);
        
        if (response.data.messages && response.data.messages.length > 0) {
          // Clear any existing messages (like welcome message)
          this.messages = [];
          
          // Add all messages from history and check their crawl status
          for (const message of response.data.messages) {
            // If this message has a job_chain_id, check if it's still running
            if (message.job_chain_id) {
              try {
                const jobChainResponse = await axios.get(`/api/jobChains/${message.job_chain_id}`);
                const jobChainData = jobChainResponse.data;
                
                // Determine if crawl is completed based on final_status
                const isCompleted = jobChainData.final_status && 
                  (jobChainData.final_status.category === 'completed' || 
                   jobChainData.final_status.category === 'failed' || 
                   jobChainData.final_status.category === 'canceled');
                
                message.crawlCompleted = isCompleted;
              } catch (error) {
                console.error('Error checking job chain status:', error);
                // If we can't check status, assume it's completed to avoid blocking the input
                message.crawlCompleted = true;
              }
            }
            
            this.addMessage(message);
          }
          
          this.historyLoaded = true;
          
          // Check if we have running crawls and start polling if needed
          if (this.hasRunningCrawls()) {
            this.startSystemPromptPolling();
          }
        }
        
        // Set the current chat name from the chat list
        const currentChat = this.chatList.find(chat => chat.id === this.chatId);
        if (currentChat) {
          this.currentChatName = currentChat.name || 'Untitled Chat';
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
        // Show error message
        this.addMessage({
          text: `Error loading chat history: ${error.response?.data?.message || 'Failed to load history'}`,
          isUser: false,
          timestamp: new Date(),
          output: null,
        });
      } finally {
        this.loading = false;
      }
    },
    
    submitQuestion() {
      if (!this.question.trim() || this.loading) return;
      
      // Add user question to chat
      this.addMessage({
        text: this.question,
        isUser: true,
        timestamp: new Date(),
        output:null,
      });
      
      // Process question
      this.ask(this.question);
      
      // Clear input
      this.question = '';
    },
    
    addMessage(message) {
      this.messages.push(message);
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    
    scrollToBottom() {
      const container = this.$refs.chatMessages;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    
    focusInput() {
      if (this.$refs.chatInput) {
        this.$refs.chatInput.focus();
      }
    },
    
    async ask(question) {
      this.loading = true;
      
      try {
        // Show loading indicator
        const loadingMsgIndex = this.messages.length;
        this.addMessage({
          text: '<i class="el-icon-loading"></i> Thinking...',
          isUser: false,
          timestamp: new Date(),
          output: null,
        });
        
        // Convert selectedMode to broad_type value
        const broadType = this.selectedMode === 'semantic-site-search' ? 2 : 1; // 2 for semantic search, 1 for content analysis
        
        // Make API call to backend
        const response = await axios.post(`/llm/chat/${this.extentId}`, {
          question,
          chat_id: this.chatId,
          broad_type: broadType
        });
        
        // Remove loading message
        this.messages.splice(loadingMsgIndex, 1);
        
        // Update chat ID if this is a new conversation
        if (!this.chatId && response.data.chat_id) {
          this.chatId = response.data.chat_id;
        }
        
        // Debug: Log the output to see its structure
        console.log('LLM Response output:', response.data.output);
        
        // Display the response
        this.addMessage({
          text: null,
          isUser: false,
          timestamp: new Date(),
          output: response.data.output,
        });
      } catch (error) {
        // Remove loading message
        this.messages.pop();
        
        // Show error message
        this.addMessage({
          text: `Error: ${error.response?.data?.message || 'Failed to get response'}`,
          isUser: false,
          timestamp: new Date()
        });
        
        console.error('Error asking question:', error);
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.focusInput();
        });
      }
    },

    /**
     * Check if any messages have job_chain_id
     */
    hasJobChainMessages() {
      return this.messages.some(message => message.job_chain_id);
    },

    /**
     * Check if there are any running crawls (job_chain_id messages that are not completed)
     */
    hasRunningCrawls() {
      // Check if we have any job_chain_id messages that don't have completed status
      return this.messages.some(message => {
        return message.job_chain_id && !message.crawlCompleted;
      });
    },
    
    /**
     * Mark a crawl as completed
     */
    markCrawlCompleted(jobChainId) {
      const message = this.messages.find(m => m.job_chain_id === jobChainId);
      if (message) {
        message.crawlCompleted = true;
        console.log(`Marked crawl ${jobChainId} as completed`);
        
        // Stop system prompt polling if no more running crawls
        if (!this.hasRunningCrawls()) {
          this.stopSystemPromptPolling();
        }
      }
    },

    /**
     * Start polling for system prompts during running crawls
     * This will be triggered on the first progress update
     */
    startSystemPromptPolling() {
      if (this.systemPromptInterval || !this.chatId) {
        return; // Already polling or no chat ID
      }
      
      console.log('Starting system prompt polling...');
      
      // Fetch the first system prompt immediately
      this.fetchSystemPrompt();
      
      // Then start the interval for subsequent messages
      this.systemPromptInterval = setInterval(() => {
        if (this.hasRunningCrawls()) {
          this.fetchSystemPrompt();
        } else {
          this.stopSystemPromptPolling();
        }
      }, 10000); // Poll every minute after the first immediate fetch
    },
    
    /**
     * Handle the first progress update to trigger immediate system prompt fetch
     */
    onFirstProgressUpdate() {
      if (!this.hasReceivedFirstProgressUpdate) {
        this.hasReceivedFirstProgressUpdate = true;
        this.startSystemPromptPolling();
      }
    },

    /**
     * Stop polling for system prompts
     */
    stopSystemPromptPolling() {
      if (this.systemPromptInterval) {
        console.log('Stopping system prompt polling...');
        clearInterval(this.systemPromptInterval);
        this.systemPromptInterval = null;
      }
    },

    /**
     * Fetch a new system prompt from the API
     */
    async fetchSystemPrompt() {
      if (!this.chatId) return;
      
      try {
        const response = await axios.get(`/llm/${this.extentId}/${this.chatId}/get_system_prompt`);
        
        if (response.data.success && response.data.system_message) {
          const systemMessage = response.data.system_message;
          
          // Mark all previous system messages as old (desaturated)
          this.messages.forEach(message => {
            if (message.isSystemMessage) {
              message.isOldSystemMessage = true;
              message.isNewSystemMessage = false;
            }
          });
          
          // Add the new system message with animation
          this.addMessage({
            text: systemMessage.text,
            isUser: false,
            timestamp: new Date(),
            output: null,
            isSystemMessage: true,
            isNewSystemMessage: true,
            isOldSystemMessage: false,
            type: systemMessage.type,
            systemMsgId: systemMessage.id,
            response: systemMessage.response
          });
          
          // Remove the animation flag after the animation completes
          setTimeout(() => {
            const lastMessage = this.messages[this.messages.length - 1];
            if (lastMessage && lastMessage.isNewSystemMessage) {
              lastMessage.isNewSystemMessage = false;
            }
          }, 4000); // Remove animation after 4 seconds
        }
      } catch (error) {
        console.error('Error fetching system prompt:', error);
        // If we get an error, stop polling
        this.stopSystemPromptPolling();
      }
    },

    /**
     * Submit a user response to a system message
     */
    async submitSystemResponse(systemMsgId) {
      const response = this.systemMessageResponses[systemMsgId];
      if (!response || response.trim() === '') {
        return;
      }
      
      try {
        const submitResponse = await axios.post(`/llm/${this.extentId}/${this.chatId}/system_message/${systemMsgId}/submit_response`, {
          response: response.trim()
        });
        
        if (submitResponse.data.success) {
          // Find the message and update it with the response
          const message = this.messages.find(m => m.systemMsgId === systemMsgId);
          if (message) {
            message.response = response.trim();
          }
          
          // Clear the input
          this.$delete(this.systemMessageResponses, systemMsgId);
          
          this.$message.success('Response submitted successfully');
        }
      } catch (error) {
        console.error('Error submitting system response:', error);
        this.$message.error('Failed to submit response');
      }
    },

    /**
     * Check for job chain messages and get updates
     */
    async checkForJobChainMessages() {
      const jobChainMessages = this.messages.filter(message => message.job_chain_id);
      
      for (const message of jobChainMessages) {
        try {
          // Get job chain info for existing job chains
          await axios.get(`/api/jobChains/${message.job_chain_id}`);
          // Additional job chain setup logic can be added here if needed
        } catch (error) {
          console.error('Error loading job chain info:', error);
        }
      }
    },

    /**
     * Submit user feedback for a message output
     */
    async submitFeedback(message, rating) {
      if (!message.output || !message.output.id) {
        console.error('Cannot submit feedback: message has no output ID');
        return;
      }
      
      // Set loading state for this message
      this.$set(message, 'feedbackLoading', true);
      
      try {
        const response = await axios.post(`/llm/chat/${this.extentId}/feedback`, {
          llm_chat_output_id: message.output.id,
          rating: rating
        });
        
        if (response.data.success) {
          // Update the message with the feedback data
          this.$set(message, 'userFeedback', {
            id: response.data.feedback.id,
            rating: rating,
            comment: null
          });
          
          // Show success message
          const feedbackType = rating === 10 ? 'positive' : 'negative';
          this.$message.success(`${feedbackType === 'positive' ? 'Positive' : 'Negative'} feedback submitted`);
        }
      } catch (error) {
        console.error('Error submitting feedback:', error);
        this.$message.error('Failed to submit feedback');
      } finally {
        // Remove loading state
        this.$set(message, 'feedbackLoading', false);
      }
    },

    /**
     * Initiate a crawl for the starting URL
     */
    async initiateCrawl() {
      try {
        this.loading = true;

        // Get site basics first
        const basicsResponse = await axios.get('/easy/basics', {
          params: { url: this.startingUrl }
        });
        const siteName = basicsResponse.data.site_name;

        // Get crawl definitions
        const definitionsResponse = await axios.get(`/extents/get_all_crawl_definitions/${this.extentId}`);
        const crawlDefinitions = definitionsResponse.data;
        
        // Find the best default definition
        let crawlDefinitionId = null;
        const extentDefinition = crawlDefinitions.find(def => def.type.label === 'extent default');
        const globalDefinition = crawlDefinitions.find(def => def.type.label === 'global default');
        
        if (extentDefinition) {
          crawlDefinitionId = extentDefinition.id;
        } else if (globalDefinition) {
          crawlDefinitionId = globalDefinition.id;
        }

        // Preflight check
        const preflightOptions = {
          extent_id: this.extentId,
          data_source_id: 1,
          crawl_definition_id: crawlDefinitionId,
          type: "crawl-easy",
          override: false,
          url: this.startingUrl
        };

        const preflightResponse = await axios.post('/api/jobChains/preflight', preflightOptions);
        
        if (preflightResponse.data.status === 'ready' || preflightResponse.data.status === 'warning') {
          // Start the crawl
          const jobChainResponse = await axios.post('/api/jobChains', preflightOptions);
          const jobChainId = jobChainResponse.data.job_chain_id;

          // Create a crawl question and output in the database
          const createCrawlResponse = await axios.post(`/llm/chat/${this.extentId}`, {
            question: `Start crawl for ${this.startingUrl}`,
            chat_id: this.chatId,
            job_chain_id: jobChainId
          });

          // Update chat ID if this is a new conversation
          if (!this.chatId && createCrawlResponse.data.chat_id) {
            this.chatId = createCrawlResponse.data.chat_id;
          }

          // Add the user's question
          this.addMessage({
            text: `Starting crawl for: ${this.startingUrl}`,
            isUser: true,
            timestamp: new Date(),
            output: null,
          });

          // Add the crawl output message with job_chain_id
          this.addMessage({
            text: null,
            isUser: false,
            timestamp: new Date(),
            output: null,
            job_chain_id: jobChainId,
            crawlCompleted: false
          });
          
          // Start polling for system prompts now that we have a running crawl
          this.startSystemPromptPolling();

          // Remove the starting URL from browser URL
          if (window.history && window.history.replaceState) {
            const url = new URL(window.location);
            url.searchParams.delete('starting_url');
            window.history.replaceState({}, '', url);
          }

        } else {
          this.addMessage({
            text: `Error: ${preflightResponse.data.errors.map(e => e.text).join(', ')}`,
            isUser: false,
            timestamp: new Date(),
            output: null,
          });
        }

      } catch (error) {
        console.error('Error initiating crawl:', error);
        this.addMessage({
          text: `Failed to start crawl: ${error.response?.data?.message || 'Unknown error'}`,
          isUser: false,
          timestamp: new Date(),
          output: null,
        });
      } finally {
        this.loading = false;
      }
    }
  },
  
  beforeDestroy() {
    // Clean up the polling interval when component is destroyed
    this.stopSystemPromptPolling();
  },
  async mounted() {
    // Load the chat list first
    await this.loadChatList();
    
    if (this.initialChatId) {
      // Load chat history if a chat ID is provided
      await this.loadChatHistory();
      
      // Check if any messages have job_chain_id and need job chain updates
      await this.checkForJobChainMessages();
    }
    
    // Handle starting URL for crawl initiation
    if (this.startingUrl && !this.hasJobChainMessages()) {
      await this.initiateCrawl();
    }
    
    // Only show welcome message if this is a new chat (no history loaded) and no starting URL
    if (!this.historyLoaded && !this.startingUrl) {
      this.addMessage({
        text: "Hello! I can help you do content analysis. Please ask a question.",
        isUser: false,
        timestamp: new Date(),
        output: null,
      });
    }
  }
}
</script>

<style scoped>
.llm-chat-container {
  display: flex;
  flex-direction: row;
  height: 600px;
  gap: 20px;
}

.chat-sidebar {
  width: 250px;
  flex-shrink: 0;
  border-right: 1px solid #ebeef5;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
}

.chat-sidebar.collapsed {
  width: 50px;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #ebeef5;
  background-color: #f9f9f9;
}

.sidebar-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.collapse-btn {
  padding: 4px;
  min-width: auto;
}

.sidebar-header h4 {
  margin: 0;
  color: #303133;
}

.chat-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.no-chats {
  text-align: center;
  color: #909399;
  padding: 20px;
}

.chat-item {
  padding: 12px;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 5px;
  transition: background-color 0.2s;
  border: 1px solid transparent;
}

.chat-item:hover {
  background-color: #f5f7fa;
}

.chat-item.active {
  background-color: #ecf5ff;
  border-color: #409eff;
}

.chat-name {
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-date {
  font-size: 12px;
  color: #909399;
}

.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chat-header {
  padding: 15px 20px;
  border-bottom: 1px solid #ebeef5;
  background-color: #fff;
}

.chat-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.chat-title-section {
  flex: 1;
  margin-right: 15px;
}

.chat-title-editable {
  margin: 0;
  color: #303133;
  font-size: 18px;
  cursor: pointer;
  transition: color 0.2s;
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
}

.chat-title-editable:hover {
  color: #409eff;
  background-color: #f5f7fa;
}

.title-input {
  max-width: 400px;
}

.delete-btn {
  flex-shrink: 0;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: #f5f7fa;
}

.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 10px;
}

.message {
  display: flex;
  margin-bottom: 20px;
  padding: 10px;
  border-radius: 8px;
}

.user-message {
  background-color: #f2f6fc;
}

.ai-message {
  background-color: #d8d8d8;
}

.system-message {
  background-color: #fff9e6;
  border-left: 4px solid #e6a23c;
}

.old-system-message {
  background-color: #f8f8f8 !important;
  border-left-color: #c0c0c0 !important;
  opacity: 0.7;
  transition: all 0.5s ease;
}

.new-system-message {
  animation: systemMessageHighlight 4s ease-in-out;
  position: relative;
  z-index: 10;
}

@keyframes systemMessageHighlight {
  0% {
    background-color: #fff2e6;
    border-left-color: #f56c6c;
    transform: scale(1.05);
    box-shadow: 0 8px 16px rgba(245, 108, 108, 0.4);
  }
  15% {
    background-color: #ffe6cc;
    border-left-color: #f39c12;
    transform: scale(1.03);
    box-shadow: 0 6px 12px rgba(243, 156, 18, 0.3);
  }
  30% {
    background-color: #fff9e6;
    border-left-color: #e6a23c;
    transform: scale(1.02);
  }
  50% {
    background-color: #fffce6;
    border-left-color: #e6a23c;
    transform: scale(1.01);
  }
  75% {
    background-color: #fff9e6;
    border-left-color: #e6a23c;
    transform: scale(1.005);
  }
  100% {
    background-color: #fff9e6;
    border-left-color: #e6a23c;
    transform: scale(1);
    box-shadow: none;
  }
}

.message-left-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 12px;
  min-width: 60px;
}

.message-avatar {
  margin-bottom: 8px;
}

.feedback-buttons-vertical {
  display: flex;
  flex-direction: row;
  gap: 2px;
  align-items: center;
  justify-content: center;
}

.feedback-buttons-vertical .feedback-btn {
  padding: 2px 4px;
  min-width: auto;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feedback-buttons-vertical .feedback-icon {
  width: 16px;
  height: 16px;
}

.message-content {
  flex: 1;
}

.message-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.message-sender {
  font-weight: bold;
}

.message-time {
  font-size: 12px;
  color: #909399;
}

.message-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.chat-input {
  padding: 15px 20px;
  border-top: 1px solid #ebeef5;
  background-color: #fff;
  flex-shrink: 0;
}

.chat-input .input-row {
  display: flex;
  gap: 12px;
  align-items: stretch;
}

.mode-selector {
  flex-shrink: 0;
}

.mode-dropdown {
  width: 200px;
}

.question-input {
  flex: 1;
}

.system-input-container {
  margin-top: 10px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #e6e6e6;
}

.input-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.system-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.system-input:focus {
  border-color: #409eff;
}

.submit-btn {
  padding: 8px 16px;
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background-color: #337ecc;
}

.submit-btn:disabled {
  background-color: #c0c4cc;
  cursor: not-allowed;
}

.system-response {
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #f0f9ff;
  border-left: 3px solid #409eff;
  border-radius: 4px;
  font-size: 14px;
  color: #303133;
}

.feedback-buttons {
  margin-top: 10px;
  display: flex;
  gap: 8px;
  align-items: center;
}

.feedback-btn {
  padding: 0px 0px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 16px;
  margin-left: 0px;
}

.feedback-btn:hover {
  background-color: #f5f7fa;
}

.feedback-btn.thumbs-up {
  color: #909399;
}

.feedback-btn.thumbs-up:hover {
  color: #67c23a;
}

.feedback-btn.thumbs-up.feedback-active {
  color: #67c23a;
  background-color: #f0f9ff;
}

.feedback-btn.thumbs-down {
  color: #909399;
}

.feedback-btn.thumbs-down:hover {
  color: #f56c6c;
}

.feedback-btn.thumbs-down.feedback-active {
  color: #f56c6c;
  background-color: #fef0f0;
}

.feedback-icon {
  width: 20px;
  height: 20px;
  display: block;
}
</style>
